import {useState, useEffect} from 'react'
import {useSearchParams} from 'react-router-dom'
import {Plus, Lock, Globe, Trash2, Settings2, Pencil, KanbanSquare} from 'lucide-react'
import {useApp} from '../context/AppContext'
import ViewToggle from '../components/board/ViewToggle'
import KanbanBoard from '../components/board/KanbanBoard'
import ListView from '../components/board/ListView'
import CalendarView from '../components/board/CalendarView'
import BoardForm from '../components/board/BoardForm'
import ColumnManager from '../components/board/ColumnManager'
import ExportMenu from '../components/board/ExportMenu'
import NewTaskForm from '../components/board/NewTaskForm'
import BoardFilters, {
  type BoardFilters as BoardFiltersType,
  defaultBoardFilters,
} from '../components/board/BoardFilters'
import ConfirmDialog from '../components/common/ConfirmDialog'
import Badge from '../components/common/Badge'
import PageHeader from '../components/common/PageHeader'

import {PageTitle} from '../../../../_metronic/layout/core'

export default function BoardPage() {
  const {state, dispatch, getBoardById, apiDeleteBoard} = useApp()
  const [searchParams] = useSearchParams()
  const boardIdFromUrl = searchParams.get('boardId')

  const [showNewBoard, setShowNewBoard] = useState(false)
  const [showEditBoard, setShowEditBoard] = useState(false)
  const [showNewTask, setShowNewTask] = useState(false)
  const [showColumnManager, setShowColumnManager] = useState(false)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [filters, setFilters] = useState<BoardFiltersType>(defaultBoardFilters)

  useEffect(() => {
    if (boardIdFromUrl && state.boards.length > 0) {
      const boardExists = state.boards.find((b) => b.id === boardIdFromUrl)

      if (boardExists && boardIdFromUrl !== state.currentBoardId) {
        dispatch({type: 'SET_BOARD', payload: boardIdFromUrl})
      }
    }
  }, [boardIdFromUrl, state.currentBoardId, state.boards, dispatch])

  const board = getBoardById(state.currentBoardId)

  if (!board) {
    return (
      <div className='devops-page'>
        <div className='devops-empty-state gap-3'>
          <p className='text-sm text-slate-400'>Nenhum quadro selecionado</p>
          <button
            onClick={() => setShowNewBoard(true)}
            className='flex items-center gap-1.5 px-4 py-2 rounded-xl text-sm font-medium text-white bg-brand-500 hover:bg-brand-600 transition-colors'
          >
            <Plus size={16} />
            Criar Quadro
          </button>
          {showNewBoard && <BoardForm onClose={() => setShowNewBoard(false)} />}
        </div>
      </div>
    )
  }

  const allStatuses = board.columns.map((c) => c.status)

  return (
    <div className='devops-page devops-page--board flex min-h-[620px] flex-col'>
      <PageTitle>DevOps - Quadro</PageTitle>
      <PageHeader
        eyebrow='Quadro de trabalho'
        icon={KanbanSquare}
        title={board.name}
        description={
          board.description || 'Organize o fluxo de trabalho e acompanhe cada etapa da entrega.'
        }
        meta={
          <Badge
            className={
              board.visibility === 'private'
                ? 'bg-amber-50 text-amber-700'
                : 'bg-slate-100 text-slate-600'
            }
          >
            {board.visibility === 'private' ? (
              <span className='flex items-center gap-1'>
                <Lock size={10} /> Privado
              </span>
            ) : (
              <span className='flex items-center gap-1'>
                <Globe size={10} /> Público
              </span>
            )}
          </Badge>
        }
        actions={
          <>
            <ViewToggle />
            <ExportMenu boardId={board.id} />
            <button
              onClick={() => setShowEditBoard(true)}
              className='devops-icon-button'
              aria-label='Editar quadro'
            >
              <Pencil size={15} />
            </button>
            <button
              onClick={() => setShowColumnManager(true)}
              className='devops-icon-button'
              aria-label='Gerenciar colunas'
            >
              <Settings2 size={16} />
            </button>
            <button onClick={() => setShowNewTask(true)} className='devops-primary-button'>
              <Plus size={15} />
              <span>Nova tarefa</span>
            </button>
            <button onClick={() => setShowNewBoard(true)} className='devops-secondary-button'>
              <Plus size={15} />
              <span className='hidden sm:inline'>Novo quadro</span>
            </button>
            {state.boards.length >= 1 && (
              <button
                onClick={() => setDeleteConfirm(true)}
                className='devops-icon-button hover:!text-red-500 hover:!border-red-200'
                aria-label='Excluir quadro'
              >
                <Trash2 size={16} />
              </button>
            )}
          </>
        }
      />

      <div className='mb-4'>
        <BoardFilters filters={filters} onChange={setFilters} allStatuses={allStatuses} />
      </div>

      <div className='flex-1 min-h-[420px] overflow-hidden'>
        {state.currentView === 'kanban' && <KanbanBoard board={board} filters={filters} />}
        {state.currentView === 'list' && <ListView board={board} filters={filters} />}
        {state.currentView === 'calendar' && <CalendarView board={board} filters={filters} />}
      </div>

      {showNewBoard && <BoardForm onClose={() => setShowNewBoard(false)} />}
      {showEditBoard && <BoardForm board={board} onClose={() => setShowEditBoard(false)} />}
      {showNewTask && (
        <NewTaskForm
          boardId={board.id}
          initialStatus={board.columns[0]?.status || 'backlog'}
          onClose={() => setShowNewTask(false)}
        />
      )}
      {showColumnManager && (
        <ColumnManager board={board} onClose={() => setShowColumnManager(false)} />
      )}

      <ConfirmDialog
        isOpen={deleteConfirm}
        title='Excluir quadro'
        message={`Tem certeza que deseja excluir o quadro "${board.name}"? Todas as tarefas associadas permanecerão no sistema.`}
        confirmLabel='Excluir'
        onConfirm={async () => {
          await apiDeleteBoard(board.id)
          setDeleteConfirm(false)
        }}
        onCancel={() => setDeleteConfirm(false)}
      />
    </div>
  )
}
